import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Avatar,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Cached as CachedIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../services/redux/slices/authSlice";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  chatStatusConfig,
  currentSessionId,
  setCurrentSessionId,
} from "../../services/redux/slices/chatSlice";
import chatServices from "../../services/api/service/chatService";
import { decrypt } from "../../utils/crypto";
import { formatTimeAgo } from "../../utils/dateFormatUtils";

// --- CONFIGURATION ---
const DEFAULT_MESSAGE_LIMIT = 50;
// const SALES_AVATAR_PLACEHOLDER = "/path/to/sales/avatar.png";

// --- INTERFACE DEFINITIONS ---
interface ChatMessage {
  id: string;
  message: string;
  source: "customer" | "sales";
  created_at: string;
  avatar?: string;
}
interface ConnectChatComponentProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
}

const ChatMessageBubble: React.FC<{ message: ChatMessage }> = memo(
  ({ message }) => {
    const isCurrentUserIsCustomer = message?.source === "customer";
    const borderRadiusStyle = {
      borderRadius: "8px",
      ...(isCurrentUserIsCustomer && { borderBottomRightRadius: 0 }),
      ...(!isCurrentUserIsCustomer && { borderBottomLeftRadius: 0 }),
    };

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isCurrentUserIsCustomer ? "flex-end" : "flex-start",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        {!isCurrentUserIsCustomer && (
          <Avatar
            // src={message.avatar || SALES_AVATAR_PLACEHOLDER}
            sx={{
              width: 32,
              height: 32,
              mr: 1,
              mb: 3,
              flexShrink: 0,
            }}
          >
            {!message.avatar && <PersonIcon />}
          </Avatar>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isCurrentUserIsCustomer ? "flex-end" : "flex-start",
            maxWidth: "75%",
          }}
        >
          <Box
            sx={{
              p: 1,
              ...borderRadiusStyle,
              bgcolor: isCurrentUserIsCustomer ? "#03c6c939" : "#f0f0f0",
              color: "#1F2233",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "0.85rem",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {message?.message}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: "#757575",
              fontSize: "10px",
              mt: 0.5,
              ...(isCurrentUserIsCustomer
                ? { mr: 0, ml: "auto" }
                : { ml: 0.5, mr: "auto" }),
            }}
          >
            {formatTimeAgo(message?.created_at)}
          </Typography>
        </Box>
      </Box>
    );
  }
);

const ConnectChatComponent: React.FC<ConnectChatComponentProps> = ({
  userName = "Customer",
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { userProfile } = useSelector(authSelector);
  const { chatAccess } = useSelector(chatStatusConfig);
  const chatId = useSelector(currentSessionId);
  const { trackingId } = useSelector((state: RootState) => state.stepper);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messageLimit, setMessageLimit] = useState(DEFAULT_MESSAGE_LIMIT);

  const scrollToBottom = (behavior: "smooth" | "auto" = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const fetchChatSessions = async () => {
    if (!trackingId) return;
    try {
      const res = await chatServices.getAllChatSessions({ trackingId });
      const latestSessionId = res.data?.data?.[0]?.id || "";
      if (latestSessionId && latestSessionId !== chatId) {
        dispatch(setCurrentSessionId(latestSessionId));
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchChatSessions();
  }, [trackingId]);

  const fetchChatMessages = useCallback(async () => {
    if (!chatId) {
      setChatMessages([]);
      return;
    }
    if (messageLimit > DEFAULT_MESSAGE_LIMIT) setLoadingChatMessages(true);
    try {
      const res = await chatServices.getMessagesForChat({
        chatId,
        limit: messageLimit,
      });
      setChatMessages(res.data?.data.reverse() || []);
    } catch (error) {
      setChatMessages([]);
    } finally {
      setLoadingChatMessages(false);
    }
  }, [chatId, messageLimit]);

  useEffect(() => {
    let intervalId: any;
    if (chatId) {
      fetchChatMessages();
      if (messageLimit === DEFAULT_MESSAGE_LIMIT) {
        intervalId = setInterval(fetchChatMessages, 5000);
      }
    }
    return () => clearInterval(intervalId);
  }, [chatId, messageLimit, fetchChatMessages]);

  useEffect(() => {
    scrollToBottom("auto");
  }, [chatMessages.length]);

  const handleSendMessage = async () => {
    const messageToSend = newMessage?.trim();
    if (!messageToSend) return;

    const tempMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      message: messageToSend,
      source: "customer",
      created_at: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, tempMessage]);
    setNewMessage("");

    try {
      if (trackingId) {
        let activeChatId = chatId;
        if (!activeChatId) {
          const res = await chatServices.createChatSession({
            key: userProfile?.id,
            trackingId,
            typeOfApplicant: "Applicant",
          });
          activeChatId = res?.data?.data?.id;
          if (activeChatId) dispatch(setCurrentSessionId(activeChatId));
        }
        if (activeChatId) {
          await chatServices.sendMessage(activeChatId, {
            source: "customer",
            message: messageToSend,
          });
          fetchChatMessages();
        }
      }
    } catch (error) {
      console.error("Failed to send:", error);
    }
  };

  const ChatMessagesSkeleton = () => (
    <Box sx={{ flexGrow: 1, px: 3, pt: 2 }}>
      {[...Array(3)].map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 2, borderRadius: "8px" }} />
      ))}
    </Box>
  );

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        // Floating position for all devices
        bottom: { xs: 16, md: 24 },
        right: { xs: 16, md: 24 },
        zIndex: 9999,
        
        // Fixed dimensions for the "popup" look
        width: { xs: "320px", sm: "360px" },
        height: { xs: "480px", sm: "500px" },
        
        // Ensure it doesn't exceed screen size on very small phones
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 32px)",
        
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: "0px 8px 30px rgba(0,0,0,0.2)",
      }}
    >
      {/* HEADER */}
      <Box sx={{ py: 1.5, px: 2, bgcolor: "#FEC10D", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#383838" }}>Hello {userName}</Typography>
          <Typography sx={{ fontSize: "14px", color: "#000000ab" }}>How can we help?</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton onClick={fetchChatMessages} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
            <CachedIcon fontSize="small" sx={{ color: "#333" }} />
          </IconButton>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: "white" }}>
            <CloseIcon fontSize="small" sx={{ color: "black" }} />
          </IconButton>
        </Box>
      </Box>

      {/* CHAT BODY */}
      <Box sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {loadingChatMessages ? (
          <ChatMessagesSkeleton />
        ) : (
          <Box
            ref={chatContainerRef}
            sx={{
              flexGrow: 1,
              px: 2,
              pt: 2,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {chatMessages.length > 0 ? (
              <>
                {chatMessages.map((msg) => (
                  <ChatMessageBubble key={msg.id} message={msg} />
                ))}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <Box sx={{ m: "auto", textAlign: "center", opacity: 0.6 }}>
                <Typography variant="body2">No messages yet.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* FOOTER */}
      <Box sx={{ p: 1.5, bgcolor: "#F5F7F8", borderTop: "1px solid #eee" }}>
        <Paper
          component="form"
          onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
          elevation={0}
          sx={{ display: "flex", alignItems: "center", borderRadius: "20px", border: "1px solid #ddd", px: 1, bgcolor: "white" }}
        >
          <InputBase
            placeholder="Type a message..."
            fullWidth
            multiline
            maxRows={2}
            value={newMessage}
            disabled={!chatAccess}
            onChange={(e) => setNewMessage(e.target.value)}
            onFocus={() => setTimeout(() => scrollToBottom("smooth"), 300)}
            sx={{ ml: 1, flex: 1, fontSize: "14px", py: 0.5 }}
          />
          <IconButton 
            type="submit" 
            disabled={!newMessage.trim()} 
            sx={{ color: "#168794" }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Paper>
  );
};

export default ConnectChatComponent;
